import 'azure-maps-control/dist/atlas.min.css';

import type atlas from 'azure-maps-control';
import type { data } from 'azure-maps-control';
import { AuthenticationType } from 'azure-maps-control';
import AzureDirectionsList from 'components/shared/azureDirectionsList/azureDirectionsList';
import { NavPlaceholder } from 'components/shared/navPlaceHolder/NavPlaceHolder';
import { AppContext } from 'context/app';
import useIsMobile from 'hooks/useIsMobile';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import type {
  AzureSetCameraOptions,
  IAzureDataSourceChildren,
  IAzureMapControls,
  IAzureMapFeature,
  IAzureMapOptions,
} from 'react-azure-maps';
import {
  AzureMap,
  AzureMapDataSourceProvider,
  AzureMapFeature,
  AzureMapLayerProvider,
  AzureMapsProvider,
} from 'react-azure-maps';
import { useTranslation } from 'react-i18next';
import getLocationLatLon from 'utils/getLocationLatLon';

interface IAzureMapViewProps {
  options?: IAzureMapOptions;
  isMobile?: boolean;
}

interface IMapMarker {
  coordinates: { lon: number; lat: number };
  title: string;
}

/**
 * Renders a point on the Azure map.
 *
 * @param {IMapMarker} marker - The marker object containing coordinates and title.
 * @returns {IAzureMapFeature} The Azure map feature representing the point.
 */
const renderPoint = (marker: IMapMarker): IAzureMapFeature => {
  const rendId = Math.random();
  return (
    <AzureMapFeature
      key={rendId}
      id={rendId.toString()}
      type="Point"
      coordinate={[marker.coordinates.lon, marker.coordinates.lat]}
      properties={{
        title: marker.title,
        icon: 'marker-red',
      }}
      variant={'shape'}
    />
  );
};

/**
 * Renders a multi-line feature on the Azure map.
 *
 * @param {data.Position[]} coordinates - An array of coordinates that define the line.
 * @returns {IAzureMapFeature} The rendered Azure map feature.
 */
const renderMultiLine = (
  coordinates: data.Position[],
  bbox: data.BoundingBox,
): IAzureMapFeature => {
  const rendId = Math.random();
  return (
    <AzureMapFeature
      key={rendId}
      id={rendId.toString()}
      type={'LineString'}
      coordinates={coordinates}
      bbox={bbox}
    />
  );
};

/**
 * LoaderComponent is a memoized functional component that displays a loading placeholder
 * for the Azure Map View. It uses the `useTranslation` hook from the `react-i18next` library
 * to fetch localized strings for the title and subtitle.
 *
 * @returns {JSX.Element} A NavPlaceholder component with loading state and localized title and subtitle.
 */
const LoaderComponent = memo(() => {
  const { t } = useTranslation('common');
  return (
    <NavPlaceholder
      mapLoading={true}
      title={t('labels.loading')}
      subtitle={t('labels.delay')}
    />
  );
});

/**
 * MapController component is a memoized React functional component that renders an Azure Map with various features.
 * It uses context to get navigation starting place and directions, and conditionally sets markers based on these values.
 * The component also adjusts map camera options and controls based on the current state.
 *
 * @component
 * @param {IAzureMapViewProps} options - The options for the Azure Map.
 * @param {boolean} isMobile - Determines if the device is mobile and adjusts layout accordingly
 *
 * @returns {JSX.Element} The rendered Azure Map with features and controls.
 *
 * @example
 * <MapController options={mapOptions} />
 *
 * @remarks
 * - Uses `useContext` to access `AppContext` for directions and navigation starting place.
 * - Uses `useEffect` to update markers based on navigation starting place and directions.
 * - Uses `useMemo` to memoize marker rendering and map camera options.
 * - Renders `AzureMap`, `AzureMapDataSourceProvider`, `AzureMapLayerProvider`, and `AzureMapFeature` components.
 */

const MapController: React.FC<IAzureMapViewProps> = memo(
  ({ options, isMobile }) => {
    const { directions, navStartingPlace } = useContext(AppContext);
    const [markers, setMarkers] = useState<IMapMarker[]>([]);

    const startCoordinates = useMemo(
      () => getLocationLatLon(navStartingPlace),
      [navStartingPlace],
    );
    useEffect(() => {
      if (startCoordinates && !directions) {
        setMarkers([{ coordinates: startCoordinates, title: 'A' }]);
      } else if (directions) {
        setMarkers([
          { coordinates: directions.routeStart, title: 'A' },
          { coordinates: directions.routeEnd, title: 'B' },
        ]);
      }
    }, [directions, startCoordinates]);

    const memoizedMarkerRender: IAzureDataSourceChildren = useMemo(
      (): any => markers.map((marker) => renderPoint(marker)),
      [markers],
    );
    const azureMapCameraOptions: AzureSetCameraOptions | undefined | null =
      useMemo(() => {
        const options: AzureSetCameraOptions = {
          center: [startCoordinates?.lon, startCoordinates?.lat],
          zoom: 16,
        };

        if (directions?.bbox) {
          options.padding = isMobile ? 25 : 75;
          options.bounds = directions.bbox;
        }
        return options;
      }, [directions, isMobile, startCoordinates]);

    const azureMapControls: IAzureMapControls[] | undefined = useMemo(
      () =>
        ['Zoom', 'Compass', 'Pitch', 'Style', 'Traffic'].map((control) => ({
          controlName: `${control}Control`,
          options: {
            position: 'top-right' as atlas.ControlPosition,
          },
        })),
      [],
    );

    const memoizedRouteLine: IAzureDataSourceChildren | null =
      useMemo((): any => {
        if (!directions) {
          return;
        }
        const coordinates = directions.features[0].geometry.coordinates[0];
        if (coordinates) {
          return renderMultiLine(
            directions.features[0].geometry.coordinates[0],
            directions.bbox,
          );
        }
      }, [directions]);

    const memoizedMapView = useMemo(
      () => (
        <div style={{ height: !isMobile ? '100%' : '79%', width: '100%' }}>
          <AzureMap
            LoaderComponent={LoaderComponent}
            options={options}
            cameraOptions={azureMapCameraOptions}
            controls={azureMapControls}
          >
            <AzureMapDataSourceProvider id={'dataSource'}>
              <AzureMapLayerProvider
                id={'lineLayer'}
                options={{
                  lineCap: 'round',
                  lineJoin: 'round',
                  strokeWidth: 5,
                  strokeColor: 'rgba(75, 75, 255, 0.75)',
                }}
                type={'LineLayer'}
              />
              {memoizedRouteLine}
              <AzureMapLayerProvider
                id={'symbolLayer'}
                options={{
                  source: 'dataSource',
                  iconOptions: {
                    allowOverlap: false,
                    image: ['get', 'icon'],
                    size: 1,
                  },
                  textOptions: {
                    font: ['SegoeUi-Bold'],
                    color: '#FFFFFF',
                    textField: ['get', 'title'],
                    offset: [0, -1.45],
                  },
                }}
                type={'SymbolLayer'}
              />
              {memoizedMarkerRender}
            </AzureMapDataSourceProvider>
          </AzureMap>
        </div>
      ),
      [
        azureMapCameraOptions,
        azureMapControls,
        isMobile,
        memoizedMarkerRender,
        memoizedRouteLine,
        options,
      ],
    );

    return (
      <>
        <div style={{ height: !isMobile ? '100%' : '79%', width: '100%' }}>
          {memoizedMapView}
        </div>
      </>
    );
  },
);

/**
 * AzureMapView component.
 *
 * This component renders an Azure Map using the Azure Maps SDK.
 * It utilizes the `useMemo` hook to memoize the map options and the `useContext` hook to access the application context.
 * If the map is loading, it displays a loader component.
 *
 * @component
 * @returns {JSX.Element} The rendered Azure Map or a loader component if the map is loading.
 *
 * @example
 * return (
 *   <AzureMapView />
 * )
 *
 * @remarks
 *  - The Azure Maps subscription key is retrieved from the environment variables.
 *  - Uses `useIsMobile` to determine if the device is mobile and adjust layout accordingly.
 *  - Conditionally renders `AzureDirectionsList` if the device is mobile.
 *
 * @see {@link https://docs.microsoft.com/en-us/azure/azure-maps/ | Azure Maps Documentation}
 */
const AzureMapView: React.FC = memo(() => {
  const isMobile = useIsMobile();
  const azureMapOptions: IAzureMapOptions = useMemo(
    () => ({
      authOptions: {
        authType: AuthenticationType.subscriptionKey,
        subscriptionKey: process.env.REACT_APP_AZURE_MAPS_PRIMARY_KEY || '',
      },
      style: 'road',
      language: 'en-US',
      showLogo: true,
      showFeedbackLink: false,
    }),
    [],
  );
  const { mapLoading } = useContext(AppContext);
  const memoizedMapList = useMemo(
    () => (isMobile ? <AzureDirectionsList /> : null),
    [isMobile],
  );
  if (mapLoading) {
    return <LoaderComponent />;
  }
  return (
    <>
      <AzureMapsProvider>
        <MapController options={azureMapOptions} isMobile={isMobile} />
      </AzureMapsProvider>
      {memoizedMapList}
    </>
  );
});

export default memo(AzureMapView);
